import React, { useState, useEffect, useMemo } from "react";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useGetWorkspaceMembersQuery, useRemoveMemberMutation, useGetWorkspaceQuery } from "../store/api/workspaces";
import { useGetTemplatesQuery, useDeleteTemplateMutation, useReorderTemplateTasksMutation } from "../store/api/templates";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import { Settings, Building, Users as UsersIcon, FileText, Plus, Edit2, Trash2, ChevronDown, ChevronRight, Clock, Calendar, CalendarDays, Info, Code, Loader2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import CreateTemplateModal from "../components/CreateTemplateModal";
import EditTemplateModal from "../components/EditTemplateModal";
import EditWorkspaceModal from "../components/EditWorkspaceModal";
import EditUserModal from "../components/EditUserModal";
import AddTaskToTemplateModal from "../components/AddTaskToTemplateModal";
import { TaskTemplate, Task, User } from "../types";
import { useTimeAgo } from "../utils/timeAgo";

// Sortable Task Row Component
interface SortableTaskRowProps {
  task: Task;
  getRecurrenceIcon: (type: string) => React.ReactNode;
  getRecurrenceLabel: (type: string) => string;
}

function SortableTaskRow({ task, getRecurrenceIcon, getRecurrenceLabel }: SortableTaskRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 ${isDragging ? 'bg-blue-50 shadow-lg opacity-90' : ''}`}
    >
      <td className="w-10 px-2 py-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-200"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>
      </td>
      <td className="px-4 py-3 w-[800px] min-w-0">
        <div className="text-sm text-gray-900 truncate" title={task.name}>
          {task.name}
        </div>
        {task.description && (
          <div className="text-xs text-gray-600 mt-0.5 truncate" title={task.description}>
            {task.description}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {getRecurrenceIcon(task.recurrence.type)}
          <span className="text-sm text-gray-700 truncate" title={getRecurrenceLabel(task.recurrence.type)}>
            {getRecurrenceLabel(task.recurrence.type)}
          </span>
        </div>
      </td>
    </tr>
  );
}

export default function SettingsPage() {
  const { currentUser } = useApp();
  const { t, language } = useLanguage();
  const { getTimeAgo } = useTimeAgo();

  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);

  // Fetch workspace details from API
  const { data: workspace, isLoading: isLoadingWorkspace } = useGetWorkspaceQuery(
    currentWorkspace?.id || '',
    { skip: !currentWorkspace?.id }
  );

  // Fetch users from API
  const { data: users = [], isLoading: isLoadingUsers, refetch: refetchUsers } = useGetWorkspaceMembersQuery(
    currentWorkspace?.id || '',
    { skip: !currentWorkspace?.id }
  );

  // Fetch templates from API
  const { data: apiTemplates = [], isLoading: isLoadingTemplates } = useGetTemplatesQuery(
    { workspaceId: currentWorkspace?.id || '' },
    { skip: !currentWorkspace?.id }
  );

  // Local state for optimistic updates during drag
  const [localTemplates, setLocalTemplates] = useState<TaskTemplate[]>([]);

  // Sync local state with API data
  useEffect(() => {
    setLocalTemplates(apiTemplates);
  }, [apiTemplates]);

  // Use local templates for rendering (supports optimistic updates)
  const taskTemplates = localTemplates;

  // Remove member mutation
  const [removeMember] = useRemoveMemberMutation();

  // Delete template mutation
  const [deleteTemplate] = useDeleteTemplateMutation();

  // Reorder tasks mutation
  const [reorderTasks] = useReorderTemplateTasksMutation();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [editWorkspaceModalOpen, setEditWorkspaceModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [addTaskToTemplate, setAddTaskToTemplate] = useState<{ id: string; name: string } | null>(null);
  const [deleteTemplateModal, setDeleteTemplateModal] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleTemplate = (templateId: string) => {
    setExpandedTemplates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const getRecurrenceIcon = (type: string) => {
    console.log(type);
    switch (type) {
      case "daily":
        return <Clock className="w-4 h-4" />;
      case "weekly":
        return <Calendar className="w-4 h-4" />;
      case "monthly":
        return <CalendarDays className="w-4 h-4" />;
      case "quarterly":
        return <CalendarDays className="w-4 h-4" />;
      case "semi-annually":
        return <CalendarDays className="w-4 h-4" />;
      case "yearly":
        return <CalendarDays className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getRecurrenceLabel = (type: string) => {
    switch (type) {
      case "none":
        return t('repeatPattern.none');
      case "daily":
        return t('repeatPattern.daily');
      case "weekly":
        return t('repeatPattern.weekly');
      case "monthly":
        return t('repeatPattern.monthly');
      case "quarterly":
        return t('repeatPattern.quarterly');
      case "semi-annually":
        return t('repeatPattern.semiAnnually');
      case "yearly":
        return t('repeatPattern.yearly');
      default:
        return type;
    }
  };

  const getRecurrenceDetails = (task: any) => {
    if (task.recurrence.type === "weekly" && task.recurrence.dayOfWeek !== undefined) {
      const days = t('common.weekDays').split(',');
      return t('tasks.everyWeek') + ' ' + days[task.recurrence.dayOfWeek];
    }
    if (task.recurrence.type === "monthly" && task.recurrence.dayOfMonth !== undefined) {
      return t('tasks.everyMonth') + ' ' + task.recurrence.dayOfMonth;
    }
    return "";
  };

  const handleDeleteTemplate = (template: TaskTemplate) => {
    setDeleteTemplateModal({ id: template.id, name: template.name });
  };

  const confirmDeleteTemplate = async () => {
    if (!currentWorkspace || !deleteTemplateModal) return;

    setIsDeleting(true);
    try {
      await deleteTemplate({ workspaceId: currentWorkspace.id, templateId: deleteTemplateModal.id }).unwrap();
      toast.success(t('settings.templateDeleted') || 'Template deleted');
      setDeleteTemplateModal(null);
    } catch (error) {
      handleApiError(error, t('settings.templateDeleteFailed') || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent, templateId: string) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;
    if (!currentWorkspace) return;

    // Find the template and reorder its tasks
    const templateIndex = localTemplates.findIndex(t => t.id === templateId);
    if (templateIndex === -1) return;

    const template = localTemplates[templateIndex];
    const oldIndex = template.tasks.findIndex(t => t.id === active.id);
    const newIndex = template.tasks.findIndex(t => t.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedTasks = arrayMove(template.tasks, oldIndex, newIndex);
      const taskIds = reorderedTasks.map(task => task.id);

      // Optimistic update - update local state immediately
      const updatedTemplates = [...localTemplates];
      updatedTemplates[templateIndex] = {
        ...template,
        tasks: reorderedTasks,
      };
      setLocalTemplates(updatedTemplates);

      try {
        // Call the backend API to persist the new order
        await reorderTasks({
          workspaceId: currentWorkspace.id,
          templateId: templateId,
          taskIds: taskIds,
        }).unwrap();

        toast.success(t('settings.tasksReordered') || 'Tasks reordered');
      } catch (error) {
        // Revert on error
        setLocalTemplates(apiTemplates);
        handleApiError(error, t('settings.reorderFailed') || 'Failed to reorder tasks');
      }
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!currentWorkspace) return;

    // Prevent self-deletion
    if (currentUser && userId === currentUser.id) {
      toast.error(t('users.cannotDeleteSelf'));
      return;
    }

    if (confirm(t('users.removeFromWorkspaceConfirm'))) {
      try {
        await removeMember({ workspaceId: currentWorkspace.id, userId }).unwrap();
        toast.success(t('users.userDeleted'));
      } catch (error) {
        handleApiError(error, t('users.userDeleteFailed'));
      }
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <div className="mb-8">
        <p className="text-gray-600">
          {t('dashboard.overview')}
        </p>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="templates">{t('settings.templates')}</TabsTrigger>
          <TabsTrigger value="tenant">{t('workspace.title')}</TabsTrigger>
          <TabsTrigger value="users">{t('users.title')}</TabsTrigger>
          <TabsTrigger value="about">
            <Info className="w-4 h-4 mr-1" />
            {t('common.info')}
          </TabsTrigger>
     
        </TabsList>

        {/* テンプレート管理 */}
        <TabsContent value="templates" className="pt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-gray-900 mb-1">{t('settings.taskTemplates')}</h2>
              <p className="text-gray-600 text-sm">
                {t('settings.taskTemplatesDesc')}
              </p>
            </div>
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4" />
              {t('settings.newTemplate')}
            </Button>
          </div>

          {isLoadingTemplates ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          ) : taskTemplates.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {t('settings.noTemplates')}
              </p>
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="w-4 h-4" />
                {t('settings.createFirstTemplate')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {taskTemplates.map((template) => {
                const isExpanded = expandedTemplates.has(template.id);

                return (
                  <div
                    key={template.id}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                  >
                    <Collapsible open={isExpanded} onOpenChange={() => toggleTemplate(template.id)}>
                      <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                        <CollapsibleTrigger className="flex items-center gap-3 flex-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-gray-900">{template.name}</h3>
                            {template.description && (
                              <p className="text-gray-600 text-sm">
                                {template.description}
                              </p>
                            )}
                          </div>
                          <Badge variant="secondary" className="ml-2">
                            {template.tasks.length} {t('settings.tasksCount')}
                          </Badge>
                        </CollapsibleTrigger>

                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddTaskToTemplate({ id: template.id, name: template.name });
                              setAddTaskModalOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTemplate(template);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>

                      <CollapsibleContent>
                        <div className="border-t border-gray-200">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleDragEnd(event, template.id)}
                          >
                            <table className="w-full table-fixed">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="w-10 px-2 py-2"></th>
                                  <th className="px-4 py-2 text-left text-xs text-gray-600 w-[800px]">
                                    {t('tasks.taskName')}
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs text-gray-600 min-w-0">
                                    {t('tasks.repeatPattern')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                <SortableContext
                                  items={template.tasks.map(t => t.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {template.tasks.map((task) => (
                                    <SortableTaskRow
                                      key={task.id}
                                      task={task}
                                      getRecurrenceIcon={getRecurrenceIcon}
                                      getRecurrenceLabel={getRecurrenceLabel}
                                    />
                                  ))}
                                </SortableContext>
                              </tbody>
                            </table>
                          </DndContext>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ワークスペース設定 */}
        <TabsContent value="tenant" className="space-y-6 pt-6">
          {isLoadingWorkspace ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          ) : workspace ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Building className="w-6 h-6 text-gray-600" />
                <h2 className="text-gray-900">{t('workspace.workspaceInformation')}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="mb-2">{t('workspace.workspaceName')}</Label>
                  <Input value={workspace.name} disabled />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2">{t('settings.timezone')}</Label>
                    <Input
                      value={workspace.timezone || ''}
                      placeholder={t('settings.timezonePlaceholder')}
                      disabled
                      className={!workspace.timezone ? 'text-gray-400' : ''}
                    />
                  </div>
                  <div>
                    <Label className="mb-2">{t('settings.fiscalYearStart')}</Label>
                    <Input
                      value={workspace.fiscalYearStartMonth ? `${workspace.fiscalYearStartMonth} ${t('settings.month')}` : ''}
                      placeholder={t('settings.fiscalYearStartPlaceholder')}
                      disabled
                      className={!workspace.fiscalYearStartMonth ? 'text-gray-400' : ''}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2">{t('settings.weekStartDay')}</Label>
                  <Input
                    value={workspace.weekStartDay !== null && workspace.weekStartDay !== undefined
                      ? (workspace.weekStartDay === 0 ? t('settings.sunday') : t('settings.monday'))
                      : ''
                    }
                    placeholder={t('settings.weekStartDayPlaceholder')}
                    disabled
                    className={workspace.weekStartDay === null || workspace.weekStartDay === undefined ? 'text-gray-400' : ''}
                  />
                </div>
              </div>

              <div className="mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditWorkspaceModalOpen(true)}
                  disabled={currentWorkspace?.role !== 'admin'}
                >
                  <Edit2 className="w-4 h-4" />
                  {t('common.edit')}
                </Button>
                {currentWorkspace?.role !== 'admin' && (
                  <p className="text-xs text-gray-500 mt-2">{t('workspace.adminOnlyEdit')}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('workspace.noWorkspaceSelected')}</p>
            </div>
          )}
        </TabsContent>

        {/* ユーザー一覧 */}
        <TabsContent value="users" className="pt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setIsCreateUserModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('users.createUser')}
            </Button>
          </div>

          {isLoadingUsers ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">
                {t('common.loading')}
              </p>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {t('users.noUsersYet')}
              </p>
              <Button onClick={() => setIsCreateUserModalOpen(true)}>
                <Plus className="w-4 h-4" />
                {t('users.createFirstUser')}
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-gray-700">
                      {t('users.title')}
                    </th>
                    <th className="px-6 py-3 text-left text-gray-700">
                      {t('users.userEmail')}
                    </th>
                    <th className="px-6 py-3 text-left text-gray-700">
                      {t('users.lastLogin')}
                    </th>
                    <th className="px-6 py-3 text-left text-gray-700">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => {
                    const getRoleLabel = (role: string) => {
                      switch (role) {
                        case 'admin':
                          return t('roles.admin');
                        case 'member':
                          return t('roles.member');
                        case 'guest':
                          return t('roles.guest');
                        default:
                          return role;
                      }
                    };

                    const getRoleBadgeColor = (role: string) => {
                      switch (role) {
                        case 'admin':
                          return 'bg-purple-100 text-purple-700';
                        case 'member':
                          return 'bg-blue-100 text-blue-700';
                        case 'guest':
                          return 'bg-gray-100 text-gray-700';
                        default:
                          return 'bg-gray-100 text-gray-700';
                      }
                    };

                    return (
                      <tr key={user.id}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-900 font-medium">{user.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${getRoleBadgeColor(user.role)}`}>
                                {getRoleLabel(user.role)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-700">{user.email}</td>
                        <td className="px-6 py-4 text-gray-600 text-sm">
                          {getTimeAgo(user.lastLoginAt)}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            // Members cannot edit or delete admins
                            const currentUserRole = currentWorkspace?.role;
                            const canManageUser = currentUserRole === 'admin' || user.role !== 'admin';
                            // Users cannot delete their own account
                            const isSelf = currentUser && user.id === currentUser.id;
                            const canDeleteUser = canManageUser && !isSelf;

                            return (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingUser(user)}
                                  disabled={!canManageUser}
                                  title={!canManageUser ? t('users.noPermissionEditAdmin') : ''}
                                >
                                  <Edit2 className={`w-4 h-4 ${!canManageUser ? 'text-gray-300' : ''}`} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.id)}
                                  disabled={!canDeleteUser}
                                  title={isSelf ? t('users.cannotDeleteSelf') : (!canManageUser ? t('users.noPermissionDeleteAdmin') : '')}
                                >
                                  <Trash2 className={`w-4 h-4 ${!canDeleteUser ? 'text-gray-300' : 'text-red-600'}`} />
                                </Button>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* このツールについて */}
        <TabsContent value="about" className="pt-6">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">G</span>
                </div>
                <div>
                  <h2 className="text-gray-900">GRIT</h2>
                  <p className="text-gray-600 text-sm">
                    {t('settings.about.tagline')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">
                {t('settings.about.overview')}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('settings.about.overviewDesc')}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">
                {t('settings.about.keyFeature')}
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  {t('settings.about.keyFeatureDesc')}
                </p>
                <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                  <p className="text-gray-900">
                    ✓ {t('settings.about.feature1')}
                  </p>
                  <p className="text-gray-900">
                    ✓ {t('settings.about.feature2')}
                  </p>
                  <p className="text-gray-900">
                    ✓ {t('settings.about.feature3')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">
                {t('settings.about.features')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 mb-1">
                      {t('settings.about.recurrencePatterns')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('settings.about.recurrencePatternsDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 mb-1">
                      {t('settings.about.guestUserFunction')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('settings.about.guestUserFunctionDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 mb-1">
                      {t('settings.about.templateFunction')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('settings.about.templateFunctionDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 mb-1">
                      {t('settings.about.multiTenant')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {t('settings.about.multiTenantDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 mb-4">
                {t('settings.about.targetUsers')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('settings.about.targetUsersDesc')}
              </p>
            </div>
          </div>
        </TabsContent>

      
      </Tabs>

      <CreateTemplateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      {editingTemplate && (
        <EditTemplateModal
          open={!!editingTemplate}
          onOpenChange={(open) => !open && setEditingTemplate(null)}
          template={editingTemplate}
        />
      )}

      {workspace && (
        <EditWorkspaceModal
          open={editWorkspaceModalOpen}
          onOpenChange={setEditWorkspaceModalOpen}
          workspace={workspace}
        />
      )}

      <EditUserModal
        open={isCreateUserModalOpen || !!editingUser}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateUserModalOpen(false);
            setEditingUser(null);
          }
        }}
        user={editingUser}
      />

      <AddTaskToTemplateModal
        open={addTaskModalOpen}
        onOpenChange={(open) => {
          setAddTaskModalOpen(open);
          if (!open) {
            setAddTaskToTemplate(null);
          }
        }}
        templateId={addTaskToTemplate?.id || ""}
        templateName={addTaskToTemplate?.name || ""}
      />

      {/* Delete Template Confirmation Modal */}
      <Dialog open={!!deleteTemplateModal} onOpenChange={(open) => !open && setDeleteTemplateModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.deleteTemplate')}</DialogTitle>
            <DialogDescription>
              {t('settings.deleteTemplateConfirmMessage') || 'Are you sure you want to delete this template? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {deleteTemplateModal && (
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{deleteTemplateModal.name}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTemplateModal(null)}
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={confirmDeleteTemplate}
                disabled={isDeleting}
              >
                {isDeleting ? t('common.loading') : t('common.yes')}
              </Button>
            </div>

          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

